import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useSiteAssets } from "@/lib/siteAssets";
import { imageUrl } from "@/components/ImageUpload";
import { toast } from "sonner";
import { Building2, Stethoscope } from "lucide-react";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const initialRole = params.get("role") === "host_company" ? "host_company" : "vendor";
    const [role, setRole] = useState(initialRole);
    const [form, setForm] = useState({
        email: "", password: "", full_name: "",
        company_name: "", business_name: "", phone: "", office_location: "",
    });
    const [busy, setBusy] = useState(false);
    const assets = useSiteAssets();

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...form, role };
            const res = await register(payload);
            toast.success("Welcome to HealthFairX!");
            if (res.role === "host_company") nav("/host");
            else nav("/vendor/profile");
        } catch (err) {
            // FastAPI returns `detail` as a string for HTTPException and as a
            // list of {msg, loc, ...} for Pydantic validation 422s. Coerce to a
            // user-readable string so sonner doesn't try to render an object
            // (which crashes React).
            const d = err?.response?.data?.detail;
            const msg = typeof d === "string"
                ? d
                : Array.isArray(d)
                    ? d.map((x) => x?.msg || JSON.stringify(x)).join("; ")
                    : (d?.msg || "Registration failed");
            toast.error(msg);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2">
            <div className="flex items-center justify-center p-8 md:p-16 order-2 md:order-1">
                <div className="w-full max-w-md">
                    <Link to="/" className="font-heading text-xl font-extrabold block mb-6">HealthFair<span className="text-brand-coral">X</span></Link>
                    <h1 className="font-heading text-3xl font-extrabold">Create your account</h1>
                    <p className="text-brand-mute mt-2 text-sm">Free to join. Cancel anytime.</p>

                    <div className="grid grid-cols-2 gap-3 mt-6" data-testid="role-selector">
                        <button type="button" data-testid="role-host" onClick={() => setRole("host_company")}
                            className={`rounded-xl border p-4 text-left transition-all ${role === "host_company" ? "border-brand-forest bg-brand-mint" : "border-brand-line hover:border-brand-forest/40"}`}>
                            <Building2 size={20} className="text-brand-forest" />
                            <div className="font-heading font-bold mt-2">Host company</div>
                            <div className="text-xs text-brand-mute">Run a fair</div>
                        </button>
                        <button type="button" data-testid="role-vendor" onClick={() => setRole("vendor")}
                            className={`rounded-xl border p-4 text-left transition-all ${role === "vendor" ? "border-brand-coral bg-brand-coral/5" : "border-brand-line hover:border-brand-coral/40"}`}>
                            <Stethoscope size={20} className="text-brand-coral" />
                            <div className="font-heading font-bold mt-2">Vendor</div>
                            <div className="text-xs text-brand-mute">Get discovered</div>
                        </button>
                    </div>

                    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
                        <div>
                            <label className="label-base">{role === "host_company" ? "Contact name" : "Your name"}</label>
                            <input data-testid="reg-name" required className="input-base" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                        </div>
                        {role === "host_company" ? (
                            <div>
                                <label className="label-base">Company name</label>
                                <input data-testid="reg-company" required className="input-base" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                            </div>
                        ) : (
                            <div>
                                <label className="label-base">Business name</label>
                                <input data-testid="reg-business" required className="input-base" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
                            </div>
                        )}
                        <div>
                            <label className="label-base">Email</label>
                            <input data-testid="reg-email" type="email" required className="input-base" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                        <div>
                            <label className="label-base">Password</label>
                            <input data-testid="reg-password" type="password" required minLength={6} className="input-base" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="label-base">Phone (optional)</label>
                                <input data-testid="reg-phone" className="input-base" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                            </div>
                            {role === "host_company" && (
                                <div>
                                    <label className="label-base">Office city</label>
                                    <input data-testid="reg-office" className="input-base" value={form.office_location} onChange={(e) => setForm({ ...form, office_location: e.target.value })} />
                                </div>
                            )}
                        </div>
                        <button data-testid="reg-submit" disabled={busy} type="submit" className="pill btn-coral w-full h-11 font-semibold disabled:opacity-50">
                            {busy ? "Creating…" : "Create account"}
                        </button>
                    </form>
                    <p className="mt-6 text-sm text-brand-mute">Have an account? <Link to="/login" data-testid="reg-go-login" className="text-brand-forest font-semibold hover:underline">Sign in</Link></p>
                </div>
            </div>
            <div className="relative hidden md:block order-1 md:order-2">
                <img src={imageUrl(assets.register_background)} className="absolute inset-0 w-full h-full object-cover" alt="" />
                <div className="absolute inset-0 bg-gradient-to-tr from-brand-forest/80 via-brand-forest/40 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-12 text-white">
                    <h2 className="font-heading text-4xl font-extrabold leading-tight">A marketplace built<br />on trust.</h2>
                </div>
            </div>
        </div>
    );
}
